import posthog, { type CaptureResult } from 'posthog-js';

/**
 * Every event the site sends. A union for the same reason as apps/web's
 * `events.ts`: a misspelt event name breaks nothing and quietly splits a
 * funnel. Pageviews are automatic; these are only what a pageview can't see.
 *
 * The outcomes — request submitted, quoted, paid, delivered — are sent by the
 * API (`apps/api/src/services`), not from here: an ad blocker must not lose a
 * sale. Do not add them here.
 */
export type ServicesEvent =
  // The intake form.
  | 'request_started'
  | 'request_invalid'
  | 'request_failed'
  // The customer's status page.
  | 'invoice_opened'
  | 'teaser_downloaded'
  | 'report_downloaded'
  | 'badge_embed_copied';

/** Ids, counts and enums only — never what somebody typed. */
export type ServicesEventProperties = Record<
  string,
  boolean | number | string | undefined
>;

/** Same super-property apps/web sets to `canvas`: one PostHog project, two
 *  products, each event attributable. */
const PLATFORM = 'services';

/**
 * `/r/:token` is the customer's status page, and the token in it is their
 * login. It must never reach an analytics tool — as a URL, a referrer, or an
 * initial-URL person property — so every string on every event is scrubbed.
 */
const STATUS_TOKEN = /\/r\/[\w-]{20,64}/g;

export function scrubTokens(value: string): string {
  return value.replace(STATUS_TOKEN, '/r/[token]');
}

function scrubObject(obj: Record<string, unknown> | undefined): void {
  if (!obj) return;
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') obj[key] = scrubTokens(value);
  }
}

export function scrubEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event;
  scrubObject(event.properties);
  scrubObject(event.$set);
  scrubObject(event.$set_once);
  return event;
}

/**
 * PostHog for the public site, with three choices made for a marketing site
 * that sells to the EU:
 *
 * - **No cookies, no storage** (`persistence: 'memory'`). Nothing is written
 *   to the visitor's device, so there is no consent banner to show. The cost:
 *   a visitor is a new anonymous person on each full page load. The site is
 *   one SPA, so a visit — landing → pricing → intake → submit — is one person.
 * - **No autocapture, no session recording.** The intake form holds names,
 *   emails and phone numbers; nothing is sent that wasn't named here.
 * - **Status tokens scrubbed** from every event (`scrubEvent`).
 *
 * Off unless `VITE_POSTHOG_KEY` is set — dev and tests send nothing.
 */
export function initAnalytics(): void {
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  if (!key) return;

  try {
    posthog.init(key, {
      api_host:
        (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ??
        'https://us.i.posthog.com',
      autocapture: false,
      before_send: scrubEvent,
      capture_pageleave: true,
      // A pageview per route change, not only the first load.
      capture_pageview: 'history_change',
      disable_session_recording: true,
      persistence: 'memory',
    });
    posthog.register({
      environment:
        (import.meta.env.VITE_ENVIRONMENT as string | undefined) ??
        'development',
      platform: PLATFORM,
    });
  } catch {
    // Analytics must never stop the site loading.
  }
}

/** This visit's anonymous id, sent with the intake so the API's outcome
 *  events (quoted, paid, delivered) land on the visitor who asked. */
export function analyticsId(): string | undefined {
  if (!posthog.__loaded) return undefined;
  try {
    return posthog.get_distinct_id();
  } catch {
    return undefined;
  }
}

export function trackEvent(
  event: ServicesEvent,
  properties?: ServicesEventProperties,
): void {
  if (!posthog.__loaded) return;
  try {
    posthog.capture(event, properties);
  } catch {
    // An event that can't be sent costs the visitor nothing.
  }
}
